import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {redirectForDownload} from 'BenchlingUtil';
import ValidationErrorsTable, {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {
  EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO,
  ErrorData,
  RowErrorCode,
  SpreadsheetValidationError,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import navigateToURL from 'lib/navigateToURL';
import {TEMPLATE_BULK_MONOMERS_CSV_LINK} from 'molbio/monomers/MonomerConstants';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_14} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';

import {
  validateMonomersFromFile,
  MonomerCsvValidationOutput,
  createMonomersFromFile,
  MonomerValidationOutput,
} from '../MonomersDAO';
import MonomerConfigurationPageContainer from './MonomerConfigurationPageContainer';

export default function MonomerCreateFromCSVPage(): JSX.Element {
  const [csvValidationOutput, setCsvValidationOutput] = useState<MonomerCsvValidationOutput | null>(null);
  const [importUrl, setImportUrl] = useState<string | null>(null);
  const [isValidationLoading, setIsValidationLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [errorData, setErrorData] = useState<ErrorData>(EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO);

  const canSubmit =
    file != null &&
    !isValidationLoading &&
    csvValidationOutput != null &&
    csvValidationOutput.csvFormattingValidationError == null &&
    csvValidationOutput.monomerValidationOutputs?.every((output) => output.isValid);

  const setErrorsForMonomerValidation = (
    monomerValidationOutputs: Array<MonomerValidationOutput> | null
  ): void => {
    if (monomerValidationOutputs == null) {
      return;
    }
    const otherErrors: Array<SpreadsheetValidationError> = [];
    for (const [row, monomerOutput] of monomerValidationOutputs.entries()) {
      if (monomerOutput.isValid) {
        continue;
      }
      const currentRowErrors: Array<SpreadsheetValidationError> = [];
      for (const errorMessage of Object.values(monomerOutput.errors)) {
        if (errorMessage != null) {
          currentRowErrors.push({row, errorCode: RowErrorCode.OTHER_ERROR, errorMessage});
        }
      }
      if (currentRowErrors.length === 0) {
        // If we didn't detect any property errors and isValid is false, we must be missing required fields.
        currentRowErrors.push({
          row,
          errorCode: RowErrorCode.OTHER_ERROR,
          errorMessage: 'Row is missing at least one required field value.',
        });
      }
      otherErrors.push(...currentRowErrors);
    }
    setErrorData({...EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO, otherErrors});
  };

  const uploadAndValidateCsv = async (file: File): Promise<void> => {
    setFile(file);
    setIsValidationLoading(true);
    const importUrl = await ImportUtil.upload(file);
    if (importUrl) {
      try {
        setImportUrl(importUrl);
        const output = await validateMonomersFromFile(file.name, importUrl);
        setCsvValidationOutput(output);
        setErrorsForMonomerValidation(output.monomerValidationOutputs);
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        if (net.isServerRequestError(e)) {
          notifications.notifyError(e);
        }
      } finally {
        setIsValidationLoading(false);
      }
    }
  };

  const createMonomers = async (): Promise<void> => {
    if (importUrl != null) {
      try {
        LoadingScreen.show('Importing...');
        const result = await createMonomersFromFile(importUrl);
        notifications.success(`Imported ${result.length} monomers`);
        navigateToURL('/monomers');
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        notifications.notifyError(e);
      } finally {
        LoadingScreen.hide();
      }
    }
  };

  const createButton = (
    <Button
      buttonStyle={ButtonStyle.SECONDARY}
      text="Import monomers"
      size={ButtonSize.MEDIUM}
      disabled={!canSubmit}
      onClick={createMonomers}
      dataTestAttrs={{
        component: 'MonomerCreateFromCSVPage',
        element: 'createButton',
      }}
    />
  );

  const fileSection = (
    <div className={css(styles.fileSection)}>
      <div className={css(styles.bannerContainer)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          // Keep in sync with MONOMER_CSV_IMPORT_COLUMN_NAMES in benchling/molbio/monomers/constants.py
          text='The CSV file must define “name”, “symbol”, "smiles", and “naturalAnalog" columns. It can also include an optional  "customMolecularWeight" column.'
          supportingInfo="Acceptable natural analog values include IUPAC bases, r, and p."
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Download CSV template',
            actionOnClick: () => {
              redirectForDownload(TEMPLATE_BULK_MONOMERS_CSV_LINK);
            },
          }}
        />
      </div>
      <div className={css(styles.fileChooserContainer)}>
        {file == null ? (
          <div className={css(styles.flex1, styles.fileChooser)}>
            <FileChooser onSelectFiles={uploadAndValidateCsv} multiple={false} />
          </div>
        ) : (
          <div className={css(styles.flex1)}>
            <FileList>
              <FileRow
                file={file}
                onRequestRemove={() => {
                  setFile(null);
                }}
              />
            </FileList>
          </div>
        )}
      </div>
    </div>
  );

  const validationSection = (
    <div>
      {file == null ? null : isValidationLoading ? (
        <CenteredHorizontalThrobber />
      ) : csvValidationOutput?.csvFormattingValidationError != null ? (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={csvValidationOutput.csvFormattingValidationError}
        />
      ) : (
        <ValidationErrorsTable
          spreadsheetType={SpreadsheetType.SPREADSHEET}
          resolvableErrorStatuses={{}}
          errorData={errorData}
        />
      )}
    </div>
  );

  return (
    <div
      className={css(styles.pageContainer)}
      {...getTestProps({component: 'MonomerCreateFromCSVPage', element: 'root'})}
    >
      <MonomerConfigurationPageContainer methodTitle="Upload CSV" />
      <div className={css(styles.middleSection)}>
        {fileSection}
        <span className={css(styles.uploadReviewText)}>Upload review</span>
        <span className={css(styles.subtext)}>Review spreadsheet interpretation and errors</span>
        {validationSection}
      </div>
      <div className={css(styles.submitBar)}>
        <div className={css(styles.buttonsContainer)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={() => {
              navigateToURL('/monomers');
            }}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          {createButton}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
    boxShadow: '0px -2px 4px rgba(92, 124, 131, 0.1)',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  middleSection: {
    marginLeft: 40,
    marginRight: 40,
    flex: 1,
    flexDirection: 'column',
    display: 'flex',
  },
  uploadReviewText: {
    ...FONT_PROXIMA_14,
    color: NEAR_BLACK,
    marginBottom: 4,
  },
  subtext: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginBottom: 16,
  },
  columnSubtext: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
  },
  fileSection: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 4,
  },
  fileChooserContainer: {
    maxWidth: 450,
    display: 'flex',
    minHeight: 100,
    alignItems: 'center',
  },
  flex1: {
    flex: 1,
  },
  fileChooser: {
    marginLeft: -10,
  },
  bannerContainer: {
    maxWidth: 1050,
  },
});
